<script>
  import { createEventDispatcher } from "svelte";
  import {
    Checkbox,
    Modal as DangerModal,
  } from "carbon-components-svelte";
  import { fetchWithSession } from "@utils/fetchWithSession.js";
  import Modal from "../../../../lib/components/Modal.svelte";
  import { isValidRegex } from "@utils/regex.js";
  import { fly } from "svelte/transition";
  import { InfoBlock, Tooltip, TextInput } from "paxray-components-svelte";

  export let open = false;
  export let currentRule = {};
  export let idproject = "";
  export let openDangerModal = false;
  export let idToDelete = null;
  export let prefillNumbersToCheck = "";
  export let prefillRequestId = 0;

  const dispatch = createEventDispatcher();

  // Two sample values, each field turns green or red when it is filled.
  let testNumbers = ["", ""];
  let lastPrefillRequestId = 0;

  $: if (!open) {
    testNumbers = ["", ""];
  }

  $: if (open && prefillRequestId !== lastPrefillRequestId) {
    const samples = String(prefillNumbersToCheck || "")
      .split(/[\n ]+/)
      .filter(Boolean);
    testNumbers = [samples[0] || "", samples[1] || ""];
    lastPrefillRequestId = prefillRequestId;
  }

  $: isFormInvalid =
    !String(currentRule.record_type ?? "").trim() ||
    !String(currentRule.prefix ?? "").trim() ||
    currentRule.length == null ||
    currentRule.length === "" ||
    Number.isNaN(Number(currentRule.length)) ||
    !Number.isInteger(Number(currentRule.length)) ||
    Number(currentRule.length) < 1;

  // Recomputed on every change of the samples and of the rule, so changing prefix or
  // length afterwards updates the colors as well. null means the field is empty.
  $: numberChecking = checkTestNumbers(
    testNumbers,
    currentRule?.prefix,
    currentRule?.length,
    currentRule?.padding,
  );

  function checkTestNumbers(numbers) {
    return numbers.map((value) => {
      const number = String(value || "").trim();
      if (!number) return null;
      return (
        isValidRegex(number) &&
        checkCorrectLength(number) &&
        checkCorrectPrefix(number)
      );
    });
  }

  function checkCorrectLength(number) {
    let inputStr = String(number);
    if (currentRule.padding) {
      inputStr = inputStr.replace(/^0+/, "") || "0";
    }
    return inputStr.length === Number(currentRule.length);
  }

  function checkCorrectPrefix(number) {
    let inputStr = String(number);
    const prefixStr = String(currentRule.prefix);

    if (currentRule.padding) {
      inputStr = inputStr.replace(/^0+/, "") || "0";
    }

    if (inputStr.startsWith(prefixStr)) return true;
    return false;
  }

  function saveData() {
    const payload = {
      record_type: currentRule.record_type,
      prefix: currentRule.prefix,
      length: Number(currentRule.length),
      padding: currentRule.padding || false,
      track: currentRule.track || false,
    };
    if (currentRule.id) {
      updateData(currentRule.id, payload);
    } else {
      createData(payload);
    }
  }

  function updateData(id, payload) {
    fetchWithSession(`/api/damprojects/number-ranges/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        open = false;
        if (data.ok) dispatch("saved", { data: data.data, isNew: false });
      })
      .catch((err) => {
        console.error("Error updating data:", err);
      });
  }

  function createData(payload) {
    payload.project_id = idproject;
    fetchWithSession(`/api/damprojects/number-ranges`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        open = false;
        if (data.ok) dispatch("saved", { data: data.data, isNew: true });
      })
      .catch((err) => {
        console.error("Error creating data:", err);
      });
  }

  function deleteData(id) {
    fetchWithSession(`/api/damprojects/number-ranges/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        openDangerModal = false;
        if (data.ok) dispatch("deleted", { id: idToDelete });
      })
      .catch((err) => {
        console.error("Error deleting data:", err);
      });
  }

  $$restProps;
</script>


<Modal
  preventCloseOnClickOutside
  actions
  autowidth
  bind:open
  modalHeading={currentRule.id ? "Edit Number Range" : "New Number Range"}
  subtitle="Define how task registration values should be recognized as business object IDs."
  primaryButtonText="Save"
  primaryButtonValidations={isFormInvalid}
  primaryButtonNotAllow={isFormInvalid}
  secondaryButtonText="Cancel"
  on:open
  primaryButtonAction={saveData}
  on:click:button--secondary={() => (open = false)}
>
  <div class="modal-content">
    <section class="form-section">
      <InfoBlock
        type="info"
        heading="Number Range"
        message="Set the name and format used to recognize matching values."
        showClose={false}
      />
      <div class="definition-fields">
        <div class="field-group record-type-field">
          <TextInput
            hideLabel
            light
            labelText="Record Type"
            required
            tooltipText="Business object or ID type that this number range represents, for example Order, Ticket, or Invoice."
            placeholder="e.g. Invoice"
            bind:value={currentRule.record_type}
          />
        </div>
        <div class="field-group prefix-field">
          <TextInput
            hideLabel
            light
            required
            tooltipText="Static start of the ID. Values must begin with this prefix after optional zero-padding is handled."
            labelText="Prefix"
            placeholder="e.g. 47"
            bind:value={currentRule.prefix}
          />
        </div>
        <div class="field-group length-field">
          <TextInput
            hideLabel
            light
            required
            tooltipText="Expected total length of the value. Enter a positive number."
            tooltipDirection="bottom"
            labelText="Length"
            placeholder="e.g. 10"
            bind:value={currentRule.length}
          />
        </div>
      </div>
    </section>

    <section class="form-section">
      <InfoBlock
        type="info"
        heading="Behavior"
        message="Choose how matching and downstream analysis should handle this range."
        showClose={false}
      />
      <div class="option-fields">
        <div class="checkbox-form">
          <Checkbox
            labelText="Ignore leading zeros"
            bind:checked={currentRule.padding}
          />
          <div>
            <Tooltip
              text="Leading zeros are removed before prefix and length are checked."
            />
          </div>
        </div>
        <div class="checkbox-form">
          <Checkbox
            id="number-range-track"
            labelText="Track matching objects"
            bind:checked={currentRule.track}
          />
          <div>
            <Tooltip
              text="Use this number range as a tracked business object in downstream analysis."
            />
          </div>
        </div>
      </div>
    </section>

    <section class="form-section">
      <InfoBlock
        type="info"
        heading="Test Numbers"
        message="Optionally enter up to two sample values to verify the configuration."
        showClose={false}
      />
      <div class="test-fields">
        {#each testNumbers as _, index}
          <div
            class="test-field"
            class:valid={numberChecking[index] === true}
            class:invalid={numberChecking[index] === false}
            title={numberChecking[index] === true
              ? "Matches this number range"
              : numberChecking[index] === false
                ? "Does not match this number range"
                : ""}
          >
            <TextInput
              hideLabel
              light
              labelText={`Test number ${index + 1}`}
              placeholder="e.g. 4700012345"
              bind:value={testNumbers[index]}
            />
            {#if numberChecking[index] === true}
              <svg class="test-mark" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3 8.5l3.2 3.2L13 4.8" />
              </svg>
            {:else if numberChecking[index] === false}
              <svg class="test-mark" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            {/if}
          </div>
        {/each}
      </div>
    </section>
  </div>
</Modal>

<DangerModal
  preventCloseOnClickOutside
  danger
  size="sm"
  bind:open={openDangerModal}
  modalHeading={`Delete number range`}
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={() => {
    deleteData(idToDelete);
    openDangerModal = false;
  }}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <div class="deleteModalBody">
    <p>Are you sure you want to delete this number range?</p>
    <span>This action cannot be undone.</span>
  </div>
</DangerModal>

<style>
  .modal-content {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .definition-fields {
    display: grid;
    grid-template-columns: minmax(12rem, 1fr) minmax(10rem, 1fr) 8rem;
    gap: var(--control-gap);
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  .checkbox-form {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .option-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(12rem, 1fr));
    gap: var(--control-gap);
  }

  .test-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(10rem, 1fr));
    gap: var(--control-gap);
  }

  :global(.checkbox-form .bx--checkbox-wrapper) {
    flex: 0 0 auto;
    width: auto;
    margin-top: 0 !important;
  }

  :global(.definition-fields .bx--text-input),
  :global(.test-fields .bx--text-input) {
    border: solid 1px var(--pax-black-inactive);
    border-radius: 0.5rem;
  }

  :global(.definition-fields .bx--text-input),
  :global(.test-fields .bx--text-input) {
    height: 48px;
  }

  :global(.test-field.valid .bx--text-input) {
    border-color: var(--pax-green);
    background-color: var(--pax-green);
  }

  :global(.test-field.invalid .bx--text-input) {
    border-color: var(--pax-red);
    background-color: var(--pax-red);
  }

  :global(.test-field.valid .bx--text-input),
  :global(.test-field.invalid .bx--text-input) {
    padding-right: 2.5rem;
  }

  .test-field {
    position: relative;
  }

  .test-mark {
    position: absolute;
    right: 0.875rem;
    /* Middle of the 48px input, measured from the bottom because the label sits above it. */
    bottom: 24px;
    width: 1rem;
    height: 1rem;
    transform: translateY(50%);
    pointer-events: none;
    fill: none;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  /* Darker shade of the field color, so the mark stays visible on it. */
  .test-field.valid .test-mark {
    stroke: color-mix(in srgb, var(--pax-green) 45%, var(--pax-black));
  }

  .test-field.invalid .test-mark {
    stroke: color-mix(in srgb, var(--pax-red) 45%, var(--pax-black));
  }

  :global(.definition-fields .bx--text-input::placeholder),
  :global(.test-fields .bx--text-input::placeholder) {
    color: var(--pax-black-inactive);
  }

  :global(.definition-fields .bx--text-input:focus),
  :global(.test-fields .bx--text-input:focus) {
    border-color: transparent;
    outline: solid var(--bw) var(--pax-black);
    color: var(--pax-black);
  }

  @media (max-width: 760px) {
    .definition-fields,
    .option-fields,
    .test-fields {
      grid-template-columns: 1fr;
    }
  }
</style>
